import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { formatCents, formatQty, type ConsumptionReportResponse } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { PeriodBar } from '@/components/PeriodBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { monthRange, useCostCenters } from '@/lib/queries';
import { downloadCsv } from '@/lib/csv';

type Row = ConsumptionReportResponse['rows'][number] & { id: string };

/**
 * Consumo de material por centro de custo.
 *
 * É o espelho físico do relatório de custo: o Financeiro diz quanto se gastou,
 * este diz quanto de material a etapa realmente consumiu. Quando os dois não
 * conversam, alguma coisa saiu do almoxarifado sem destino — e é isso que a
 * tela existe para tornar visível.
 */
export function ConsumoPage() {
  const costCenters = useCostCenters();
  const initial = monthRange();

  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [costCenterId, setCostCenterId] = useState('');
  const [groupBy, setGroupBy] = useState<'costCenter' | 'item'>('costCenter');

  const reportQuery = useQuery({
    queryKey: ['stock-consumption', { from, to, costCenterId, groupBy }],
    queryFn: () =>
      api.get<ConsumptionReportResponse>('/stock/consumption', {
        from,
        to,
        costCenterId: costCenterId || undefined,
        groupBy,
      }),
  });

  const rows: Row[] = (reportQuery.data?.rows ?? []).map((row) => ({ ...row, id: row.key }));
  const totals = reportQuery.data?.totals;

  const columns: Array<Column<Row>> = [
    { key: 'code', label: 'Código', mono: true, render: (row) => row.code ?? '—' },
    {
      key: 'label',
      label: groupBy === 'costCenter' ? 'Centro de custo' : 'Item',
      strong: true,
      render: (row) => row.label,
    },
    {
      key: 'movementCount',
      label: 'Saídas',
      right: true,
      mono: true,
      render: (row) => row.movementCount.toLocaleString('pt-BR'),
    },
    {
      key: 'quantityMilli',
      label: 'Quantidade',
      right: true,
      mono: true,
      render: (row) => (row.unit ? `${formatQty(row.quantityMilli)} ${row.unit}` : '—'),
    },
    {
      key: 'totalCostCents',
      label: 'Custo',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.totalCostCents),
    },
    {
      key: 'sharePercent',
      label: 'Participação',
      right: true,
      mono: true,
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 60,
              height: 6,
              borderRadius: 3,
              background: T.line,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                position: 'absolute',
                inset: 0,
                width: `${Math.min(100, row.sharePercent)}%`,
                background: T.mare,
              }}
            />
          </span>
          {row.sharePercent.toFixed(1).replace('.', ',')}%
        </span>
      ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Consumo por centro de custo"
        meta={
          totals
            ? [
                { label: 'Consumido', value: formatCents(totals.totalCostCents) },
                { label: 'Saídas', value: totals.movementCount.toLocaleString('pt-BR') },
              ]
            : undefined
        }
        actions={
          <Button
            icon={Download}
            disabled={rows.length === 0}
            onClick={() =>
              downloadCsv(`consumo-${from}-a-${to}`, [
                ['Código', groupBy === 'costCenter' ? 'Etapa' : 'Item', 'Saídas', 'Quantidade', 'Custo', '%'],
                ...rows.map((row) => [
                  row.code ?? '',
                  row.label,
                  String(row.movementCount),
                  formatQty(row.quantityMilli),
                  formatCents(row.totalCostCents),
                  row.sharePercent.toFixed(1).replace('.', ','),
                ]),
              ])
            }
          >
            Exportar CSV
          </Button>
        }
      />

      <div className="workspace__body">
        <PeriodBar from={from} to={to} onFrom={setFrom} onTo={setTo}>
          <select
            className="filterbar__select"
            value={costCenterId}
            onChange={(event) => setCostCenterId(event.target.value)}
            aria-label="Centro de custo"
          >
            <option value="">Centro de custo: todos</option>
            {(costCenters.data ?? []).map((center) => (
              <option key={center.id} value={center.id}>
                {center.code} · {center.name}
              </option>
            ))}
          </select>
          <div className="filterbar__spacer" />
          <button
            type="button"
            className={`filterbar__chip${groupBy === 'costCenter' ? ' is-active' : ''}`}
            onClick={() => setGroupBy('costCenter')}
          >
            Por etapa
          </button>
          <button
            type="button"
            className={`filterbar__chip${groupBy === 'item' ? ' is-active' : ''}`}
            onClick={() => setGroupBy('item')}
          >
            Por item
          </button>
        </PeriodBar>

        {reportQuery.isPending && <LoadingState label="Somando as saídas do período…" />}
        {reportQuery.isError && (
          <ErrorState message={errorMessage(reportQuery.error)} onRetry={() => void reportQuery.refetch()} />
        )}
        {reportQuery.data && (
          <SmartDataTable
            caption="Consumo de material por centro de custo"
            columns={columns}
            rows={rows}
            emptyState={
              <EmptyState
                title="Nenhuma saída no período"
                description="Registre as saídas de material em Movimentações › Saída para consumo."
              />
            }
          />
        )}
      </div>
    </>
  );
}
